/**
 * rosterEntries — the roster board's view model, derived from SHIFT ROWS.
 *
 * This replaces foldRoster, which built the board by folding a fenced
 * `shift_commands` block out of a chat log. A chat log is not a roster: it
 * could not be read by anyone who was not in the conversation, it could not be
 * filtered by a period, and two clients folding the same history could disagree
 * about what the ward was working. The roster is `GET /shifts` now, and this
 * module is the projection from that page onto the nurse x day grid.
 *
 * Two functions, both pure, both unit-tested:
 *   - shiftsToEntries     one shift row -> one cell.
 *   - planItemsToEntries  one roster plan item -> one proposed cell.
 */
import type { Occurrence, OccurrenceDisplay, OccurrenceStatus } from '@/api/endpoints/plans'
import type { RosterPlanItem } from '@/api/endpoints/rosterPlan'
import type { ShiftResponse, ShiftStatus } from '@/api/endpoints/shifts'

/**
 * One cell of the board: one person, one roster day, one shift.
 *
 * `id` addresses the thing the cell IS: a shift id for a published row, a plan
 * ITEM id for a proposed one. Both are real and both can be acted on, which is
 * why one field carries either. `shift` is the raw shift-type code, and the
 * board renders it as written.
 *
 * `date` is the roster-day LABEL and is only ever used to place the cell in a
 * column. Every question about a WINDOW — who is on at 22:40 — is answered from
 * `plannedStart`/`plannedEnd`, because a night shift labelled the 14th is
 * worked across the following midnight.
 */
export interface RosterEntry {
  id: string
  /** Display label for the person. Falls back to the user id when no name resolved. */
  nurse: string
  /** Platform user id of the teammate. Always present: a shift names its user. */
  nurseId: string
  /** Roster-day label, `YYYY-MM-DD`. A LABEL, never a window. */
  date: string
  /** The shift-type code. */
  shift: string
  /**
   * What a board draws for this cell: the shift type's name in the reader's
   * locale, its colour token and its declared window, RESOLVED BY THE PLANNER
   * from the category's declaration.
   *
   * It is the only way a client learns them — a category's policy is not served
   * over the API — so a cell without one draws its bare code rather than a
   * label this app invented. Absent for an item of a plan not yet in force,
   * which has no occurrence at all.
   */
  display?: OccurrenceDisplay
  /**
   * What actually happened to this cell, folded from the plan's change log.
   * Absent for a plan not in force. `scheduled` is a real answer and means
   * nobody has acted on it yet.
   */
  execution?: OccurrenceStatus
  status: ShiftStatus | 'proposed'
  /** The plan that proposed this cell; null for a shift the ward is working. */
  proposal: string | null
  plannedStart: string | null
  plannedEnd: string | null
}

/** `YYYY-MM-DD`. A row whose label is malformed gets no column, so it is dropped. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Stable render order: by (date, nurse), independent of the page's order. */
function sortEntries(entries: RosterEntry[]): RosterEntry[] {
  return entries.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1
    return a.nurse.localeCompare(b.nurse)
  })
}

/**
 * Project a page of shifts onto board cells.
 *
 * `names` maps a user id to a display label (from `GET /teams/{id}/members`
 * `user_ref.display`). A user with no entry renders as their id — an honest
 * absence, never a fabricated name.
 *
 * `cancelled` shifts are dropped: a cancelled shift is not worked, so leaving
 * it on the grid would show a nurse as rostered when the ward has been told she
 * is not. Every other status is kept and carried on the cell.
 */
export function shiftsToEntries(
  shifts: readonly ShiftResponse[] | undefined | null,
  names?: ReadonlyMap<string, string>,
): RosterEntry[] {
  if (!shifts) return []
  const entries: RosterEntry[] = []
  for (const s of shifts) {
    if (s.status === 'cancelled') continue
    const nurseId = s.user || s.nurse_id
    if (!nurseId || !s.shift_type) continue
    if (!ISO_DATE_RE.test(s.date)) continue
    entries.push({
      id: s.id,
      nurseId,
      nurse: names?.get(nurseId) ?? nurseId,
      date: s.date,
      shift: s.shift_type,
      status: s.status,
      // A shift row is what the ward is working, never a proposal: a proposal
      // lives in the plan now and reaches the board through planItemsToEntries.
      proposal: null,
      plannedStart: s.planned_start,
      plannedEnd: s.planned_end,
    })
  }
  return sortEntries(entries)
}

/**
 * Project a roster plan's items onto board cells.
 *
 * The same shape as a published shift, deliberately: a proposal and the roster
 * it would become are the same grid, and a board that drew them from two
 * different shapes would drift the first time either gained a field. What
 * separates them is `status`, which is `proposed` for an item nobody has
 * published, and `proposal`, which names the plan the item belongs to.
 */
export function planItemsToEntries(
  planId: string,
  items: readonly RosterPlanItem[] | undefined | null,
  names?: ReadonlyMap<string, string>,
  occurrences?: ReadonlyMap<string, Occurrence>,
  inForce = false,
): RosterEntry[] {
  if (!items) return []
  const entries: RosterEntry[] = []
  for (const item of items) {
    const nurseId = item.user.startsWith('users/') ? item.user.slice('users/'.length) : ''
    if (!nurseId || !item.shift_type_code) continue
    if (!ISO_DATE_RE.test(item.day_label)) continue
    // One item occurs once, on its own day, so its occurrence is found by its
    // item id alone. A plan not in force has none, and the cell then carries
    // neither a display nor an execution state — which is the truth about it.
    const occurrence = occurrences?.get(item.id)
    entries.push({
      id: item.id,
      nurseId,
      nurse: names?.get(nurseId) ?? nurseId,
      date: item.day_label,
      shift: item.shift_type_code,
      display: occurrence?.display,
      execution: occurrence?.status,
      // A PUBLISHED PLAN IS NOT A PROPOSAL. Its items are what the ward is
      // working — publishing creates no shift row anywhere, so these cells are
      // the roster itself and naming a plan that "proposed" them would read as
      // something still awaiting a decision.
      status: inForce ? 'planned' : 'proposed',
      proposal: inForce ? null : planId,
      plannedStart: item.planned_start,
      plannedEnd: item.planned_end,
    })
  }
  return sortEntries(entries)
}
